import * as React from "react";
import Styled from "styled-components/macro";
import Media from "@uizard-io/styled-media-query";
import Image, { GatsbyImageProps } from "gatsby-image";

import Button from "../../../../components/buttons/Button";
import MediaQuery from "../../../layout/MediaQuery";
import Tagline from "../../../texts/Tagline";
import Content from "../../../layout/Content/Content";
import Headline from "../../../texts/Headline";
import Description from "../../../texts/Description";
import RichDescription from "../../../texts/RichDescription";

const Container = Styled.div`
  margin: 6rem 0 6rem 0;
  overflow: hidden; // fix for columns overflow

   ${Media.greaterThan("small")`
     margin: 10rem 0 10rem 0;
  `}
  
  ${Media.greaterThan("medium")`
     margin: 15rem 0 15rem 0;
     overflow: visible;
  `}
`;

const Columns = Styled.div`
  ${Media.greaterThan("700px")`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 42rem;
  `}
  
  ${Media.greaterThan("large")`
    height: 51.3rem;
  `}
`;

const Column = Styled.div`
  position: relative;
  
  ${Media.greaterThan("700px")`
    width: 45%;
    min-width: 0;
  `}
`;

const ColumnIllustration = Styled(Column)`
  height: 20rem;
  margin-top: 10rem;
  width: 100%;
  display: flex;
  align-items: center;

  ${Media.greaterThan("medium")`
    height: 42rem;
    margin-top: 15rem;
  `}
      
  ${Media.greaterThan("700px")`
      height: auto;
      width: 50%;
      margin-top: 0;
  `}
  
  position: relative;
`;

const Text = Styled.div`
  width: 43rem;
  max-width: 100%;

  ${Media.greaterThan("large")`
    width: 100%;
    padding-left: 9.5rem;
  `}
`;

const RichDescriptionExtended = Styled(RichDescription)`
  margin-bottom: 5rem;

  ${Media.greaterThan("700px")`
    margin-bottom: 0;
  `}
`;

const ImageMobile = Styled(Image)`
  width: 29rem;
  margin: 0 0 3rem 0;
`;

const TaglineExtended = Styled(Tagline)`
  color: #7f7f7f;
`;

const ImageWrapper = Styled.div`
  position: absolute;
  left: 50%;
  top: 38%;
  transform: translate(-50%, -50%);
  
  ${Media.greaterThan("700px")`
    top: 55%;
  `}
  
  ${Media.greaterThan("medium")`
    top: 50%;
  `}
`;

const ImageDesktop = Styled(Image)`
  width: 28.8rem;
  max-width: calc(100% - 0.75rem);

  ${Media.greaterThan("700px")`
    max-width: 96%;
  `}

  ${Media.greaterThan("large")`
    width: 46rem;
  `}
`;

const ExtendedButton = Styled(Button)`
  margin: 0 0 3rem 0;

  ${Media.greaterThan("medium")`
    margin: 5rem 0 5rem 0;
  `}
`;

const NavButtonLink = Styled.a`
  display: block;
`;

type HeroBigTextBannerProps = {
  tagline?: string;
  headline: string;
  description: string;
  imageA: GatsbyImageProps;
  imageB: GatsbyImageProps;
  imageAlt: string;
  link?: string;
  cta?: string;
  tagAsTitle?: boolean;
};

export default ({
  tagline,
  headline,
  description,
  imageA,
  imageB,
  imageAlt,
  link,
  cta,
  tagAsTitle,
}: HeroBigTextBannerProps) => (
  <Container>
    <Content>
      <Columns>
        <Column>
          <Text>
            {tagline && tagAsTitle && (
              <TaglineExtended as="h1">{tagline}</TaglineExtended>
            )}
            {tagAsTitle && <Headline as="h2">{headline}</Headline>}

            {tagline && !tagAsTitle && (
              <TaglineExtended>{tagline}</TaglineExtended>
            )}
            {!tagAsTitle && <Headline>{headline}</Headline>}

            <RichDescriptionExtended content={description} />

            {link && cta && cta.indexOf("Sign up") !== -1 && (
              <NavButtonLink href={link}>
                <ExtendedButton solid>{cta ? cta : "Read more"}</ExtendedButton>
              </NavButtonLink>
            )}

            {(link && cta === undefined) ||
              (cta && cta.indexOf("Sign up") === -1 && (
                <NavButtonLink href={link}>
                  <ExtendedButton variant="primarylight" solid>
                    {cta ? cta : "Read more"}
                  </ExtendedButton>
                </NavButtonLink>
              ))}
          </Text>
        </Column>

        <ColumnIllustration>
          <ImageWrapper>
            <MediaQuery lessThan="medium">
              <ImageMobile
                fluid={"fluid" in imageB ? imageB.fluid : null}
                alt={imageAlt}
              />
            </MediaQuery>

            <MediaQuery greaterThan="medium">
              <ImageDesktop
                fluid={"fluid" in imageA ? imageA.fluid : null}
                alt={imageAlt}
              />
            </MediaQuery>
          </ImageWrapper>
        </ColumnIllustration>
      </Columns>
    </Content>
  </Container>
);
